export type ChartSeries = {
  key: string;
  label?: string;
  color?: string;
};

export type ChartSpec = {
  type?: string;
  title?: string;
  data?: Array<Record<string, unknown>>;
  xKey?: string;
  yKeys?: ChartSeries[];
  nameKey?: string;
  valueKey?: string;
  [key: string]: unknown;
};

export type ParsedAssistantContent = {
  text: string;
  chart: ChartSpec | null;
  actions: string[];
};

export function parseAssistantContent(content: string): ParsedAssistantContent {
  const { text: textWithoutActions, actions } = parseActions(content);
  const { text, chart } = parseChart(textWithoutActions);

  return { text, chart, actions };
}

export function parseActions(content: string): {
  text: string;
  actions: string[];
} {
  const match = content.match(/\[ACTIONS:\s*([^\]]+)\]\s*$/);
  if (!match) return { text: content, actions: [] };

  const [, rawActions] = match;
  const actions = rawActions
    .split("|")
    .map((action) => action.trim())
    .filter(Boolean);
  const text = content.slice(0, match.index).trimEnd();

  return { text, actions };
}

export function parseChart(content: string): {
  text: string;
  chart: ChartSpec | null;
} {
  const marker = "[CHART:";
  const markerStart = content.indexOf(marker);
  if (markerStart === -1) return { text: content, chart: null };

  const jsonStart = content.indexOf("{", markerStart + marker.length);
  if (jsonStart === -1) return { text: content, chart: null };

  const jsonEnd = findJsonObjectEnd(content, jsonStart);
  if (jsonEnd === -1) return { text: content, chart: null };

  try {
    const parsed = JSON.parse(content.slice(jsonStart, jsonEnd + 1));
    if (!isChartSpec(parsed)) return { text: content, chart: null };

    const tagEnd = findChartTagEnd(content, jsonEnd);
    const text = `${content.slice(0, markerStart)}${content.slice(tagEnd)}`.trimEnd();
    return { text, chart: parsed };
  } catch {
    return { text: content, chart: null };
  }
}

function findJsonObjectEnd(content: string, jsonStart: number): number {
  let depth = 0;
  let inString = false;
  let isEscaped = false;

  for (let index = jsonStart; index < content.length; index += 1) {
    const char = content[index];

    if (inString) {
      if (isEscaped) {
        isEscaped = false;
      } else if (char === "\\") {
        isEscaped = true;
      } else if (char === "\"") {
        inString = false;
      }
      continue;
    }

    if (char === "\"") {
      inString = true;
      continue;
    }

    if (char === "{") {
      depth += 1;
      continue;
    }

    if (char === "}") {
      depth -= 1;
      if (depth === 0) return index;
    }
  }

  return -1;
}

function findChartTagEnd(content: string, jsonEnd: number): number {
  let tagEnd = jsonEnd + 1;

  while (content[tagEnd] && /\s/.test(content[tagEnd])) {
    tagEnd += 1;
  }

  return content[tagEnd] === "]" ? tagEnd + 1 : tagEnd;
}

function isChartSpec(value: unknown): value is ChartSpec {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
